// src/router/teacherGuard.js
import { getCurrentUser } from "@/services/auth.service";
import { isTestModeAllowed } from "@/config/testMode";

/**
 * Navigation guard for teacher-only routes (`meta.requiresTeacher`).
 *
 * D-3b: the Teacher Studio routes were `requiresAuth` only, so a signed-in
 * student could open /studio and see the teacher UI. Nothing leaked — every
 * roster query self-scopes to the caller's uid and firestore.rules enforce the
 * same boundary — but a student landing on an empty Teacher Studio is a bug in
 * its own right, and "no data leaks today" is not something a route should be
 * relying on tomorrow.
 *
 * Mirrors adminGuard: it runs AFTER the auth guard, so an unauthenticated user
 * has already been sent to /login and is simply passed through here.
 */
export const teacherGuard = async (to, from, next) => {
  if (!to.matched.some((record) => record.meta.requiresTeacher)) {
    next();
    return;
  }

  // Cypress test mode (build-gated; never active in production).
  if (isTestModeAllowed()) {
    const mockUser = window.__getAuthState ? window.__getAuthState() : null;
    if (mockUser && !mockUser.isTeacher) {
      next({ path: "/dashboard", query: { unauthorized: "teacher" } });
      return;
    }
    next();
    return;
  }

  const currentUser = getCurrentUser();

  // Not authenticated yet — the auth guard owns that redirect.
  if (!currentUser) {
    next();
    return;
  }

  if (currentUser.isTeacher) {
    next();
    return;
  }

  next({ path: "/dashboard", query: { unauthorized: "teacher" } });
};

export default teacherGuard;
